import { UserPlus01Icon } from '@langchain/untitled-ui-icons';
import { Button } from '@mui/joy';

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSAMLEmptyState } from '@/hooks/useSAMLEmptyState';
import {
  useClaimPendingOrgInvite,
  useDeletePendingOrgInvite,
  useOrganizationId,
} from '@/hooks/useSwr';
import { appOrganizationPath } from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { SAMLEmptyStateTooltip } from '../SAMLEmptyStateTooltip';
import { SAMLEmptyStateWrapper } from '../SAMLEmptyStateWrapper';
import useToast from '../Toast/useToast';
import { NavLink } from './NavLink';
import { GroupedInviteSchema, useGroupedOrgInvites } from './useGroupedInvites';

type Props = GroupedInviteSchema & {
  type: 'workspace' | 'organization';
  onClose?: () => void;
};

export function PendingInvite({
  id,
  display_name,
  workspaceInvites,
  showWorkspaces,
  type,
  onClose,
}: Props) {
  const deleteInvite = useDeletePendingOrgInvite();
  const claimInvite = useClaimPendingOrgInvite();
  const { createToast } = useToast();

  return (
    <div
      key={id}
      className="flex flex-col gap-3 rounded-xl bg-zinc-100 p-4 dark:bg-[#25252D] dark:bg-opacity-100"
    >
      {showWorkspaces ? (
        <div className="text-xs font-medium">
          You were invited to workspaces{' '}
          <strong>
            {workspaceInvites?.map((w) => w.display_name).join(', ')}
          </strong>{' '}
          in the <strong>{display_name}</strong> organization
        </div>
      ) : (
        <div className="text-xs font-medium">
          You were invited to the <strong>{display_name}</strong> {type}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          aria-label="decline"
          color="neutral"
          variant="outlined"
          loading={deleteInvite.isMutating}
          disabled={claimInvite.isMutating || deleteInvite.isMutating}
          onClick={async () => {
            try {
              await deleteInvite.trigger(id);
              onClose?.();
            } catch (error) {
              console.error('Failed to decline invite:', error);
              createToast({
                title: 'Failed to decline invite',
                description:
                  'Please try again or contact support if the problem persists.',
                type: 'error',
              });
              onClose?.();
            }
          }}
        >
          Decline
        </Button>
        <Button
          size="sm"
          aria-label="accept"
          color="primary"
          loading={claimInvite.isMutating}
          disabled={claimInvite.isMutating || deleteInvite.isMutating}
          onClick={async () => {
            try {
              await claimInvite.trigger(id);
              onClose?.();
            } catch (error) {
              console.error('Failed to accept invite:', error);
              createToast({
                title: 'Failed to accept invite',
                description:
                  'Please try again or contact support if the problem persists.',
                type: 'error',
              });
              onClose?.();
            }
          }}
        >
          Accept
        </Button>
      </div>
    </div>
  );
}

export function InviteNotification(props: { expanded: boolean }) {
  const [open, setOpen] = useState(false);
  const iconClassname = props.expanded ? 'size-3' : 'size-4';
  const loc = useLocation();
  const pendingInvites = useGroupedOrgInvites({
    revalidateOnFocus: false,
  });

  const { isSAMLEmptyState } = useSAMLEmptyState();

  const hasInvites = pendingInvites.data && pendingInvites.data.length > 0;
  const navigate = useNavigate();
  const organizationId = useOrganizationId();
  const orgPrefix = organizationId
    ? `/${appOrganizationPath}/${organizationId}`
    : '';

  const navLink = (
    <NavLink
      useExactMatch
      key="Invitations"
      location={loc}
      label="Invitations"
      onClick={(_, e) => {
        if (!hasInvites || isSAMLEmptyState) {
          e.preventDefault();
          if (isSAMLEmptyState) {
            // For SAML users in empty state, don't navigate anywhere
            return;
          }
          navigate(`${orgPrefix}/settings/members`);
        }
      }}
      expanded={props.expanded}
      icon={<UserPlus01Icon className={iconClassname} />}
      disableTooltip={isSAMLEmptyState}
    />
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger className="relative">
        <SAMLEmptyStateTooltip>
          <SAMLEmptyStateWrapper>{navLink}</SAMLEmptyStateWrapper>
        </SAMLEmptyStateTooltip>
        {hasInvites && !isSAMLEmptyState && (
          <div
            className={cn(
              'absolute flex size-4 items-center justify-center rounded-md bg-red-500 text-white',
              props.expanded ? 'right-3 top-2' : 'bottom-5 right-3'
            )}
          >
            <span className="text-xxs">{pendingInvites.data?.length}</span>
          </div>
        )}
      </PopoverTrigger>
      <PopoverContent side="right" align="end" className="h-52 overflow-y-auto">
        <div className="mb-2">Invitations</div>
        <div className="flex flex-wrap gap-4">
          {pendingInvites.data?.map((t) => (
            <PendingInvite
              key={t.id}
              {...t}
              type="organization"
              onClose={() => setOpen(false)}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
